import { Dialog, DialogContent, DialogHeader } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ExternalLink } from 'lucide-react';
import { sanitizeUrl } from '@/lib/utils';

interface ChecklistItem {
  id: string;
  target: boolean;
  ready: boolean;
  insert: boolean;
  item: string;
  comment: string;
  url?: string;
}

interface MobileTemplateViewerProps {
  isOpen: boolean;
  onClose: () => void;
  item: ChecklistItem | null;
  hideCommercialElements?: boolean;
}

const renderNotes = (notes: string) => {
  if (!notes) return null;
  
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = notes.split(urlRegex);
  
  return parts.map((part, index) => {
    if (urlRegex.test(part)) {
      const sanitizedUrl = sanitizeUrl(part);
      return (
        <a
          key={index}
          href={sanitizedUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:text-primary-dark underline break-all"
        >
          {part}
        </a>
      );
    }
    return part;
  });
};

export const MobileTemplateViewer = ({ isOpen, onClose, item, hideCommercialElements = false }: MobileTemplateViewerProps) => {
  if (!item) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] md:max-h-[90vh] min-h-0 md:min-h-[600px] flex flex-col p-0 bg-background overflow-y-auto">
        <div className="flex items-center justify-center p-4 border-b bg-muted/50 shrink-0">
          <h2 className="text-lg font-normal text-foreground">Item Details</h2>
        </div>
        
        <div className="flex-1 overflow-auto p-4 space-y-6">
          {/* Item Section */}
          <div className="space-y-2">
            <label className="text-sm font-normal text-foreground">
              Item Description
            </label>
            <div className="bg-muted/30 rounded-lg p-4">
              <p className="text-base leading-relaxed">{item.item}</p>
            </div>
          </div>

          {/* Notes Section */}
          <div className="space-y-2">
            <label className="text-sm font-normal text-foreground">
              Notes
            </label>
            {item.comment ? (
              <div className="bg-muted/30 rounded-lg p-4">
                <div className="text-base leading-relaxed">
                  {renderNotes(item.comment)}
                </div>
              </div>
            ) : (
              <div className="bg-muted/30 rounded-lg p-4">
                <p className="text-base text-muted-foreground italic">No notes available</p>
              </div>
            )}
          </div>

          {/* Shop Button Section */}
          {!hideCommercialElements && (
            <div className="space-y-2">
              <label className="text-sm font-normal text-foreground">
                Purchase Link
              </label>
              {item.url ? (
                <Button 
                  onClick={() => window.open(sanitizeUrl(item.url || ''), '_blank', 'noopener,noreferrer')}
                  variant="accent"
                  className="w-full h-12 text-base font-normal"
                  size="lg"
                >
                  <ExternalLink className="w-5 h-5 mr-2" />
                  Shop for This Item
                </Button>
              ) : (
                <div className="bg-muted/30 rounded-lg p-4">
                  <p className="text-base text-muted-foreground italic">Purchase link not available</p>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};